import { Component, useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../auth/AuthContext';
import { API_BASE, s, authHeaders } from './sharedStyles';
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';

class AnalyticsErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error('AnalyticsPage crashed:', error, info); }
  render() {
    if (this.state.error) {
      return (
        <div style={{ ...s.main, textAlign: 'center' }}>
          <p style={{ color: '#EF4444', fontWeight: 600 }}>Analytics failed to load.</p>
          <p style={{ color: '#94A3B8', fontSize: '0.85rem' }}>{String(this.state.error?.message || this.state.error)}</p>
          <button onClick={() => this.setState({ error: null })} style={s.outlineBtn}>Retry</button>
        </div>
      );
    }
    return this.props.children;
  }
}

const CHART_TOOLTIP = {
  contentStyle: { background: '#1E293B', border: '1px solid #334155', borderRadius: 8, color: '#F8FAFC', fontSize: '0.85rem' },
  labelStyle: { color: '#94A3B8', fontWeight: 600 },
};

function AnalyticsPageBody() {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState('');
  const [days, setDays] = useState(30);

  const [dailyRaw, setDailyRaw] = useState([]);
  const [calledRaw, setCalledRaw] = useState([]);
  const [sessionsRaw, setSessionsRaw] = useState([]);

  // Fetch tenant list once. Defensive: coerce non-array responses to []
  // so the dropdown .map() below never throws on malformed data.
  useEffect(() => {
    fetch(`${API_BASE}/api/v1/admin/tenants`, { headers: authHeaders(token) })
      .then(r => r.ok ? r.json() : [])
      .then(data => setTenants(Array.isArray(data) ? data : []))
      .catch(() => setTenants([]));
  }, [token]);

  const buildQs = useCallback(() => {
    const params = new URLSearchParams({ days: String(days) });
    if (tenantId) params.set('tenantId', tenantId);
    return params.toString();
  }, [days, tenantId]);

  // Fetch all three data sets. Defensive array coercion — a 500 or a
  // proxy HTML error page would otherwise leave the state as a non-array
  // and crash the chart memos below.
  useEffect(() => {
    const qs = buildQs();
    const headers = authHeaders(token);
    const arr = (data) => Array.isArray(data) ? data : [];

    fetch(`${API_BASE}/api/v1/admin/stats/daily?${qs}`, { headers })
      .then(r => r.ok ? r.json() : []).then(d => setDailyRaw(arr(d))).catch(() => setDailyRaw([]));

    fetch(`${API_BASE}/api/v1/admin/stats/called?${qs}`, { headers })
      .then(r => r.ok ? r.json() : []).then(d => setCalledRaw(arr(d))).catch(() => setCalledRaw([]));

    fetch(`${API_BASE}/api/v1/admin/stats/sessions?${qs}`, { headers })
      .then(r => r.ok ? r.json() : []).then(d => setSessionsRaw(arr(d))).catch(() => setSessionsRaw([]));
  }, [token, buildQs]);

  // Pivot daily rows into stacked chart data
  const ticketChartData = useMemo(() => {
    const map = {};
    dailyRaw.forEach(({ date, outcome, count }) => {
      if (!map[date]) map[date] = { date, COMPLETED: 0, SKIPPED: 0, OTHER: 0 };
      const bucket = outcome === 'COMPLETED' ? 'COMPLETED' : outcome === 'SKIPPED' ? 'SKIPPED' : 'OTHER';
      map[date][bucket] += count;
    });
    return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
  }, [dailyRaw]);

  // Called tickets — simple line
  const calledData = useMemo(() =>
    [...calledRaw].sort((a, b) => a.date.localeCompare(b.date)),
  [calledRaw]);

  // Sessions — simple line
  const sessionsData = useMemo(() =>
    [...sessionsRaw].sort((a, b) => a.date.localeCompare(b.date)),
  [sessionsRaw]);

  return (
    <div>
      <main style={s.main}>
        <h1 style={s.pageTitle}>{t('monitor.analytics.title')}</h1>

        {/* Filter bar */}
        <div style={s.filterBar}>
          <div style={s.filterGroup}>
            <label style={s.filterLabel}>Tenant</label>
            <select style={s.filterSelect} value={tenantId} onChange={e => setTenantId(e.target.value)}>
              <option value="">{t('monitor.analytics.allTenants')}</option>
              {tenants.map(tn => (
                <option key={tn.id} value={tn.id}>{tn.name || tn.slug || tn.id}</option>
              ))}
            </select>
          </div>
          <div style={s.filterGroup}>
            <label style={s.filterLabel}>Period</label>
            <select style={s.filterSelect} value={days} onChange={e => setDays(Number(e.target.value))}>
              <option value={7}>{t('monitor.analytics.days7')}</option>
              <option value={30}>{t('monitor.analytics.days30')}</option>
              <option value={90}>{t('monitor.analytics.days90')}</option>
            </select>
          </div>
        </div>

        {/* Chart 1: New Tickets — stacked area */}
        <div style={s.card}>
          <div style={s.cardTitle}>{t('monitor.analytics.newTickets')}</div>
          {ticketChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={ticketChartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" tick={{ fill: '#94A3B8', fontSize: 12 }} tickLine={{ stroke: '#334155' }} />
                <YAxis allowDecimals={false} tick={{ fill: '#94A3B8', fontSize: 12 }} tickLine={{ stroke: '#334155' }} />
                <Tooltip {...CHART_TOOLTIP} />
                <Legend wrapperStyle={{ color: '#94A3B8', fontSize: '0.85rem' }} />
                <Area type="monotone" dataKey="COMPLETED" stackId="1" stroke="#10B981" fill="#10B981" fillOpacity={0.6} name={t('settings.stats.completed')} />
                <Area type="monotone" dataKey="SKIPPED" stackId="1" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.6} name={t('settings.stats.skipped')} />
                <Area type="monotone" dataKey="OTHER" stackId="1" stroke="#EF4444" fill="#EF4444" fillOpacity={0.6} name={t('settings.stats.cancelledAbandoned')} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p style={{ color: '#64748B', textAlign: 'center', padding: '2rem 0' }}>No data</p>
          )}
        </div>

        {/* Chart 2: Called Tickets — line */}
        <div style={s.card}>
          <div style={s.cardTitle}>{t('monitor.analytics.calledTickets')}</div>
          {calledData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={calledData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" tick={{ fill: '#94A3B8', fontSize: 12 }} tickLine={{ stroke: '#334155' }} />
                <YAxis allowDecimals={false} tick={{ fill: '#94A3B8', fontSize: 12 }} tickLine={{ stroke: '#334155' }} />
                <Tooltip {...CHART_TOOLTIP} />
                <Line type="monotone" dataKey="count" stroke="#06B6D4" strokeWidth={2} dot={{ fill: '#06B6D4', r: 3 }} name={t('monitor.analytics.calledTickets')} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p style={{ color: '#64748B', textAlign: 'center', padding: '2rem 0' }}>No data</p>
          )}
        </div>

        {/* Chart 3: Employee Logins — line */}
        <div style={s.card}>
          <div style={s.cardTitle}>{t('monitor.analytics.employeeLogins')}</div>
          {sessionsData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={sessionsData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" tick={{ fill: '#94A3B8', fontSize: 12 }} tickLine={{ stroke: '#334155' }} />
                <YAxis allowDecimals={false} tick={{ fill: '#94A3B8', fontSize: 12 }} tickLine={{ stroke: '#334155' }} />
                <Tooltip {...CHART_TOOLTIP} />
                <Line type="monotone" dataKey="count" stroke="#A855F7" strokeWidth={2} dot={{ fill: '#A855F7', r: 3 }} name={t('monitor.analytics.employeeLogins')} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p style={{ color: '#64748B', textAlign: 'center', padding: '2rem 0' }}>No data</p>
          )}
        </div>
      </main>
    </div>
  );
}

export default function AnalyticsPage() {
  return (
    <AnalyticsErrorBoundary>
      <AnalyticsPageBody />
    </AnalyticsErrorBoundary>
  );
}
